import { useState } from "react";
import { X, Question, Bug, Sparkle, ChatCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ModalPortal from "@/components/ModalPortal";

const KIND_OPTIONS = [
  { key: "bug", label: "Buggrapport", icon: Bug, hint: "Något funkar inte som förväntat" },
  { key: "feature", label: "Önskemål", icon: Sparkle, hint: "Idé eller förbättring" },
  { key: "question", label: "Fråga", icon: Question, hint: "Behöver du hjälp?" },
  { key: "other", label: "Annat", icon: ChatCircle, hint: "Övrig återkoppling" },
];

/**
 * Modal med tre delar:
 *  1) Snabbhjälp — vanliga frågor
 *  2) Feedback-formulär — skickar till admin via Resend (POST /api/feedback)
 *  3) Direktkontakt via mail som fallback
 */
export default function HelpModal({ open, onClose }) {
  const { user } = useAuth();
  const [kind, setKind] = useState("question");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("help"); // "help" | "feedback"

  if (!open) return null;

  const reset = () => {
    setSubject("");
    setMessage("");
    setKind("question");
    setTab("help");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (subject.trim().length < 2) {
      toast.error("Skriv en kort rubrik");
      return;
    }
    if (message.trim().length < 5) {
      toast.error("Skriv gärna några ord till om vad du menar");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        kind,
        subject: subject.trim(),
        message: message.trim(),
        url: typeof window !== "undefined" ? window.location.href : undefined,
      });
      toast.success("Tack! Vi hör av oss så snart vi kan.");
      reset();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte skicka just nu");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalPortal>
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
      data-testid="help-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-app">
          <div>
            <h2 className="font-heading text-2xl text-text-primary">Hjälp & feedback</h2>
            <p className="text-sm text-text-muted mt-1">
              Vi vill gärna höra hur vi kan göra Tidsrapporten bättre för dig.
            </p>
          </div>
          <button
            data-testid="help-modal-close"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-hover text-text-muted"
            aria-label="Stäng"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex border-b border-app text-sm">
          <button
            data-testid="help-tab-guide"
            onClick={() => setTab("help")}
            className={`flex-1 px-5 py-3 font-medium transition-colors ${
              tab === "help"
                ? "text-text-primary border-b-2 border-[var(--primary,#c25934)]"
                : "text-text-muted hover:text-text-primary"
            }`}
          >
            Snabbhjälp
          </button>
          <button
            data-testid="help-tab-feedback"
            onClick={() => setTab("feedback")}
            className={`flex-1 px-5 py-3 font-medium transition-colors ${
              tab === "feedback"
                ? "text-text-primary border-b-2 border-[var(--primary,#c25934)]"
                : "text-text-muted hover:text-text-primary"
            }`}
          >
            Skicka feedback
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {tab === "help" ? (
            <div className="space-y-5 text-sm" data-testid="help-quick-guide">
              <FaqItem
                q="Hur stämplar jag in?"
                a="Gå till Stämpelklockan, välj ett objekt, välj ett moment och tryck Stämpla in. Vill du byta moment mitt i passet gör du det direkt i den aktiva sessionen — tiden räknas automatiskt om."
              />
              <FaqItem
                q="Var hittar jag mina rapporter?"
                a="Alla admins kan se allas rapporter under Rapporter i sidomenyn. Anställda ser sina egna via Dashboard. Du kan exportera till PDF eller CSV."
              />
              <FaqItem
                q="Vad gör Papperskorgen?"
                a="Raderade objekt, moment och rapporter ligger kvar i 30 dagar innan de raderas permanent. Du som admin kan återställa dem när som helst under den perioden."
              />
              <FaqItem
                q="Hur bjuder jag in fler anställda?"
                a="Gå till Anställda → Lägg till användare. Personen får en inbjudan via e-post och kan välja sitt eget lösenord."
              />
              <FaqItem
                q="Hur ändrar jag prenumeration eller kort?"
                a="Klicka på Fakturering i sidomenyn — där öppnas Stripes kundportal där du kan byta kort, se fakturor eller säga upp."
              />
              <div className="pt-3 border-t border-app text-sm text-text-muted">
                Hittar du inte svar? Klicka på <strong className="text-text-primary">Skicka feedback</strong> ovan — vi svarar oftast samma dag.
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="feedback-form">
              <div>
                <label className="text-xs uppercase tracking-wider text-text-muted">
                  Typ av återkoppling
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                  {KIND_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const active = kind === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        data-testid={`feedback-kind-${opt.key}`}
                        onClick={() => setKind(opt.key)}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          active
                            ? "border-[var(--primary,#c25934)] bg-[#fff4ef]"
                            : "border-app hover:bg-surface-hover"
                        }`}
                        title={opt.hint}
                      >
                        <Icon size={18} weight={active ? "fill" : "regular"} className="text-[var(--primary,#c25934)]" />
                        <div className="mt-1 text-sm font-medium text-text-primary">{opt.label}</div>
                        <div className="text-[11px] text-text-muted mt-0.5 leading-tight">{opt.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label htmlFor="feedback-subject" className="text-xs uppercase tracking-wider text-text-muted">
                  Rubrik
                </label>
                <input
                  id="feedback-subject"
                  data-testid="feedback-subject-input"
                  type="text"
                  required
                  maxLength={160}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="tk-input w-full mt-1"
                  placeholder={
                    kind === "bug"
                      ? "T.ex. PDF-export laddar inte"
                      : kind === "feature"
                      ? "T.ex. Notiser vid nya rapporter"
                      : "Kort beskrivning"
                  }
                />
              </div>

              <div>
                <label htmlFor="feedback-message" className="text-xs uppercase tracking-wider text-text-muted">
                  Meddelande
                </label>
                <textarea
                  id="feedback-message"
                  data-testid="feedback-message-input"
                  required
                  maxLength={4000}
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="tk-input w-full mt-1 resize-none"
                  placeholder={
                    kind === "bug"
                      ? "Vad hände? Vilket steg utlöste felet?"
                      : "Berätta gärna vad du tänker — vi läser allt."
                  }
                />
                <div className="mt-1 text-[11px] text-text-muted text-right">
                  {message.length} / 4000
                </div>
              </div>

              <div className="text-xs text-text-muted bg-surface rounded-lg p-3 border border-app">
                Vi ser: {user?.name} &lt;{user?.email}&gt; · {user?.role === "admin" ? "Admin" : "Anställd"}
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="tk-btn-outline"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  data-testid="feedback-submit-button"
                  disabled={submitting}
                  className="tk-btn-primary inline-flex items-center gap-2"
                >
                  <PaperPlaneTilt size={16} />
                  {submitting ? "Skickar…" : "Skicka"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}

function FaqItem({ q, a }) {
  return (
    <div>
      <div className="font-medium text-text-primary">{q}</div>
      <p className="text-text-muted mt-1 leading-relaxed">{a}</p>
    </div>
  );
}
